import { Editor } from '@tiptap/core'
import { StarterKit } from '@tiptap/starter-kit'
import { HardBreak } from '@tiptap/extension-hard-break'
import { Link } from '@tiptap/extension-link'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { BubbleMenu } from '@tiptap/extension-bubble-menu'
import { Placeholder } from '@tiptap/extension-placeholder'
import { TaskList } from '@tiptap/extension-task-list'
import { TaskItem } from '@tiptap/extension-task-item'
import { initLinkManager, showLinkPopover, trackRecentCrd } from './link-manager.js'
import { gfm } from 'turndown-plugin-gfm'
import { CommentMark } from './comment-mark.js'
import { showCommentPopover } from './comments.js'

let editor = null;
let slashMenuElement = null;
let slashMenuPosition = null;

const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced'
});

// Enable GitHub Flavored Markdown (tables, strikethrough, etc.)
turndownService.use(gfm);

// Configure marked with GitHub Flavored Markdown
marked.setOptions({
  gfm: true,
  breaks: false
});

// Convert checkbox HTML to TipTap TaskList format
function convertCheckboxesToTipTap(html) {
  // marked.js with GFM creates: <li><input type="checkbox" [checked] [disabled]> Text</li>
  
  // Use DOM parsing for more reliable conversion
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Find all li elements with checkbox inputs
  tempDiv.querySelectorAll('li').forEach(li => {
    const checkbox = li.querySelector('input[type="checkbox"]');
    if (checkbox) {
      // Check if the checkbox is checked (handles both checked="" and checked="checked")
      const isChecked = checkbox.hasAttribute('checked') || checkbox.checked;
      
      // Remove the input element
      checkbox.remove();
      
      // Set the data attributes for TipTap
      li.setAttribute('data-type', 'taskItem');
      li.setAttribute('data-checked', isChecked ? 'true' : 'false');
      
      // console.log(`🔲 Converted checkbox: checked=${isChecked}, text="${li.textContent.trim().substring(0, 50)}"`);
    }
  });
  
  // Find ul elements that contain taskItems and mark them as taskLists
  tempDiv.querySelectorAll('ul').forEach(ul => {
    if (ul.querySelector('[data-type="taskItem"]')) {
      ul.setAttribute('data-type', 'taskList');
    }
  });
  
  return tempDiv.innerHTML;
}

// Detect if text contains markdown formatting
function detectMarkdown(text) {
  if (!text) return false;
  
  const lines = text.split('\n');
  
  for (const line of lines) {
    const trimmed = line.trim();
    
    // Check for headings (# ## ###)
    if (/^#{1,6}\s+.+/.test(trimmed)) {
      return true;
    }
    
    // Check for bullet lists (- or *)
    if (/^[-*]\s+.+/.test(trimmed)) {
      return true;
    }
    
    // Check for numbered lists (1. 2. etc)
    if (/^\d+\.\s+.+/.test(trimmed)) {
      return true;
    }
    
    // Check for task lists (- [ ] or - [x])
    if (/^[-*]\s+\[([ x])\]\s+.+/.test(trimmed)) {
      return true;
    }
  }
  
  return false;
}

// Add task list support to turndown for TipTap's format
// TipTap outputs: <ul data-type="taskList"><li data-type="taskItem" data-checked="true/false">
turndownService.addRule('taskListItems', {
  filter: function (node) {
    return node.nodeName === 'LI' && 
           node.getAttribute('data-type') === 'taskItem';
  },
  replacement: function (content, node) {
    const isChecked = node.getAttribute('data-checked') === 'true';
    // Trim content to remove leading/trailing whitespace and ensure inline output
    const trimmedContent = content.trim();
    return (isChecked ? '- [x] ' : '- [ ] ') + trimmedContent + '\n';
  }
});

turndownService.addRule('taskLists', {
  filter: function (node) {
    return node.nodeName === 'UL' && 
           node.getAttribute('data-type') === 'taskList';
  },
  replacement: function (content, node) {
    return '\n' + content + '\n';
  }
});

// Slash commands
const slashCommands = [
  { label: 'Heading 1', desc: 'Large heading', search: 'heading h1', action: (editor) => editor.chain().focus().toggleCode(false).setHeading({ level: 1 }).run() },
  { label: 'Heading 2', desc: 'Medium heading', search: 'heading h2', action: (editor) => editor.chain().focus().toggleCode(false).setHeading({ level: 2 }).run() },
  { label: 'Heading 3', desc: 'Small heading', search: 'heading h3', action: (editor) => editor.chain().focus().toggleCode(false).setHeading({ level: 3 }).run() },
  { label: 'Table', desc: 'Insert table', search: 'table', action: (editor) => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  { label: 'Bullet List', desc: 'Unordered list', search: 'bullet list ul', action: (editor) => editor.chain().focus().toggleBulletList().run() },
  { label: 'Numbered List', desc: 'Ordered list', search: 'numbered list ol ordered', action: (editor) => editor.chain().focus().toggleOrderedList().run() },
  { label: 'Task List', desc: 'Checklist with checkboxes', search: 'task list checklist checkbox todo', action: (editor) => editor.chain().focus().toggleTaskList().run() },
  { label: 'Divider', desc: 'Horizontal rule', search: 'divider line hr rule', action: (editor) => editor.chain().focus().setHorizontalRule().run() },
];

let selectedIndex = 0;
let searchQuery = '';
let filteredCommands = [];

function filterCommands(query) {
  if (!query) return slashCommands;
  const q = query.toLowerCase();
  return slashCommands.filter(cmd => 
    cmd.label.toLowerCase().includes(q) || 
    cmd.search.toLowerCase().includes(q) ||
    cmd.desc.toLowerCase().includes(q)
  );
}

function renderSlashMenu(commands) {
  if (!slashMenuElement) return;
  
  slashMenuElement.innerHTML = commands.map((cmd, idx) => `
    <div class="slash-menu-item ${idx === selectedIndex ? 'selected' : ''}" data-index="${idx}">
      <div>
        <div class="label">${cmd.label}</div>
        <div class="desc">${cmd.desc}</div>
      </div>
    </div>
  `).join('');
  
  // Re-attach click handlers
  slashMenuElement.querySelectorAll('.slash-menu-item').forEach((item, idx) => {
    item.addEventListener('click', () => {
      executeSlashCommand(commands[idx]);
    });
  });
}

function showSlashMenu(editor, from) {
  if (!slashMenuElement) {
    slashMenuElement = document.createElement('div');
    slashMenuElement.className = 'slash-menu';
    document.body.appendChild(slashMenuElement);
  }
  
  searchQuery = '';
  selectedIndex = 0;
  filteredCommands = filterCommands(searchQuery);
  renderSlashMenu(filteredCommands);
  
  const coords = editor.view.coordsAtPos(from);
  slashMenuElement.style.position = 'fixed';
  slashMenuElement.style.top = (coords.bottom + 4) + 'px';
  slashMenuElement.style.left = coords.left + 'px';
  slashMenuElement.style.display = 'block';
  slashMenuPosition = from;
}

function hideSlashMenu() {
  if (slashMenuElement) {
    slashMenuElement.style.display = 'none';
  }
  slashMenuPosition = null;
  searchQuery = '';
  selectedIndex = 0;
  filteredCommands = [];
}

function updateSlashMenu(query) {
  searchQuery = query;
  selectedIndex = 0;
  filteredCommands = filterCommands(query);
  renderSlashMenu(filteredCommands);
  
  // If no results, hide menu
  if (filteredCommands.length === 0) {
    hideSlashMenu();
  }
}

function executeSlashCommand(command) {
  if (slashMenuPosition !== null) {
    // Calculate the length to delete (/ + search query)
    const deleteLength = 1 + searchQuery.length;
    
    editor.chain()
      .focus()
      .deleteRange({ from: slashMenuPosition, to: slashMenuPosition + deleteLength })
      .run();
    
    command.action(editor);
    hideSlashMenu();
  }
}

function createBubbleMenu() {
  const bubbleMenuEl = document.createElement('div');
  bubbleMenuEl.className = 'bubble-menu';
  bubbleMenuEl.innerHTML = `
    <button data-cmd="bold" id="bubble-bold" title="Bold">
      <i class="ph ph-text-bolder"></i>
    </button>
    <button data-cmd="italic" id="bubble-italic" title="Italic">
      <i class="ph ph-text-italic"></i>
    </button>
    <button data-cmd="code" id="bubble-code" title="Code">
      <i class="ph ph-code"></i>
    </button>
    <button data-cmd="link" id="bubble-link" title="Link">
      <i class="ph ph-link"></i>
    </button>
    <button data-cmd="comment" id="bubble-comment" title="Add comment">
      <i class="ph ph-chat-circle-dots"></i>
    </button>
    <div class="divider"></div>
    <div class="heading-dropdown">
      <button data-cmd="heading-toggle" id="bubble-heading" title="Heading">
        <i class="ph ph-text-h"></i>
        <i class="ph ph-caret-down" style="font-size: 12px;"></i>
      </button>
      <div class="heading-dropdown-content" id="headingDropdown">
        <button data-cmd="paragraph">Paragraph</button>
        <button data-cmd="h1">Heading 1</button>
        <button data-cmd="h2">Heading 2</button>
        <button data-cmd="h3">Heading 3</button>
      </div>
    </div>
    <div class="divider"></div>
    <button data-cmd="bulletList" id="bubble-bullet" title="Bullet List">
      <i class="ph ph-list-bullets"></i>
    </button>
    <button data-cmd="orderedList" id="bubble-ordered" title="Numbered List">
      <i class="ph ph-list-numbers"></i>
    </button>
  `;
  
  // Use mousedown instead of click to maintain focus
  bubbleMenuEl.addEventListener('mousedown', (e) => {
    e.preventDefault(); // Prevent editor from losing focus
    
    const button = e.target.closest('button');
    if (!button) return;
    
    const cmd = button.dataset.cmd;
    if (!cmd) return;
    
    // Close heading dropdown if clicking elsewhere
    if (cmd !== 'heading-toggle') {
      document.getElementById('headingDropdown')?.classList.remove('show');
    }
    
    executeEditorCommand(cmd);
  });
  
  return bubbleMenuEl;
}

function executeEditorCommand(cmd) {
  if (!editor) return;
  
  switch(cmd) {
    case 'bold':
      editor.chain().focus().toggleBold().run();
      break;
    case 'italic':
      editor.chain().focus().toggleItalic().run();
      break;
    case 'code':
      editor.chain().focus().toggleCode().run();
      break;
    case 'link':
      console.log('🔗 Link button clicked!');
      console.log('showLinkPopover function:', showLinkPopover);
      console.log('editor exists:', !!editor);
      const existingHref = editor.getAttributes('link').href || '';
      console.log('calling showLinkPopover with href:', existingHref);
      showLinkPopover(editor, existingHref);
      console.log('showLinkPopover called');
      break;
    case 'comment':
      const { from, to } = editor.state.selection;
      if (from === to) return; // No selection
      
      const selectedText = editor.state.doc.textBetween(from, to, ' ');
      if (!selectedText.trim()) return;
      
      // Show comment creation popover
      console.log('💬 Comment button clicked, selected text:', selectedText);
      showCommentPopover(selectedText, from, to);
      break;
    case 'heading-toggle':
      document.getElementById('headingDropdown')?.classList.toggle('show');
      break;
    case 'paragraph':
      // Remove code mark explicitly, then set paragraph
      if (editor.isActive('code')) {
        editor.chain().focus().unsetMark('code').setParagraph().run();
      } else {
        editor.chain().focus().setParagraph().run();
      }
      document.getElementById('headingDropdown')?.classList.remove('show');
      break;
    case 'h1':
      if (editor.isActive('code')) {
        editor.chain().focus().unsetMark('code').setHeading({ level: 1 }).run();
      } else {
        editor.chain().focus().setHeading({ level: 1 }).run();
      }
      document.getElementById('headingDropdown')?.classList.remove('show');
      break;
    case 'h2':
      if (editor.isActive('code')) {
        editor.chain().focus().unsetMark('code').setHeading({ level: 2 }).run();
      } else {
        editor.chain().focus().setHeading({ level: 2 }).run();
      }
      document.getElementById('headingDropdown')?.classList.remove('show');
      break;
    case 'h3':
      if (editor.isActive('code')) {
        editor.chain().focus().unsetMark('code').setHeading({ level: 3 }).run();
      } else {
        editor.chain().focus().setHeading({ level: 3 }).run();
      }
      document.getElementById('headingDropdown')?.classList.remove('show');
      break;
    case 'bulletList':
      editor.chain().focus().toggleBulletList().run();
      break;
    case 'orderedList':
      editor.chain().focus().toggleOrderedList().run();
      break;
  }
}

// Close dropdown when clicking outside
document.addEventListener('click', (e) => {
  if (!e.target.closest('.heading-dropdown')) {
    document.getElementById('headingDropdown')?.classList.remove('show');
  }
});
export async function initEditor(content) {
  console.log('🎯 initEditor called');
  
  // Initialize link manager
  console.log('📎 Calling initLinkManager...');
  await initLinkManager();
  console.log('✅ initLinkManager complete');
  
  console.log('🏗️ Creating editor...');
  
  const editorArea = document.getElementById('editorArea');
  editorArea.innerHTML = '<div id="tiptapEditor"></div>';
  
  const bubbleMenuEl = createBubbleMenu();
  editorArea.appendChild(bubbleMenuEl);
  
  editor = new Editor({
    element: document.getElementById('tiptapEditor'),
    extensions: [
      StarterKit.configure({
        hardBreak: false,  // Disable default hardBreak from StarterKit
        link: false,  // Disable Link in StarterKit since we're adding it separately
        taskList: false,  // Disable TaskList in StarterKit so we can configure it separately
      }),
      HardBreak.extend({
        addKeyboardShortcuts() {
          return {
            'Shift-Enter': () => this.editor.commands.setHardBreak(),
          }
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: 'task-item-wrapper',
        },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      CommentMark,
      Placeholder.configure({
        placeholder: "Type '/' for commands...",
      }),
      BubbleMenu.configure({
        element: bubbleMenuEl,
        shouldShow: ({ editor, view, state, from, to }) => {
          const checks = {
            editable: view.editable,
            hasFocus: view.hasFocus(),
            visible: state.selection.visible,
            fromTo: `${from}-${to}`,
            hasSelection: from < to,
            selectedText: state.doc.textBetween(from, to, ' ').substring(0, 20)
          };
          
          console.log('🎈 shouldShow called:', checks);
          
          if (!view.editable) {
            console.log('❌ Not editable');
            return false;
          }
          if (!view.hasFocus()) {
            console.log('❌ No focus');
            return false;
          }
          if (from === to) {
            console.log('❌ No selection (from === to)');
            return false;
          }
          
          const selectedText = state.doc.textBetween(from, to, ' ');
          if (!selectedText || selectedText.trim().length === 0) {
            console.log('❌ No text selected');
            return false;
          }
          
          console.log('✅ SHOWING BUBBLE');
          return true;
        },
        tippyOptions: {
          duration: 100,
          placement: 'top',
        },
      }),
    ],
    content: convertCheckboxesToTipTap(marked.parse(content)),
    onUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection;
      
      // Check if slash menu is currently open
      if (slashMenuPosition !== null) {
        // Get text from slash position to cursor
        const text = editor.state.doc.textBetween(slashMenuPosition, to, ' ');
        
        // If text doesn't start with /, close menu
        if (!text.startsWith('/')) {
          hideSlashMenu();
          return;
        }
        
        // Extract search query (everything after the /)
        const query = text.slice(1);
        updateSlashMenu(query);
      } else {
        // Check if user just typed /
        const text = editor.state.doc.textBetween(from - 1, to, ' ');
        if (text === '/') {
          showSlashMenu(editor, from - 1);
        }
      }
    },
  });
  
  editor.on('selectionUpdate', () => {
    document.getElementById('bubble-bold')?.classList.toggle('is-active', editor.isActive('bold'));
    document.getElementById('bubble-italic')?.classList.toggle('is-active', editor.isActive('italic'));
    document.getElementById('bubble-code')?.classList.toggle('is-active', editor.isActive('code'));
    document.getElementById('bubble-link')?.classList.toggle('is-active', editor.isActive('link'));
    document.getElementById('bubble-bullet')?.classList.toggle('is-active', editor.isActive('bulletList'));
    document.getElementById('bubble-ordered')?.classList.toggle('is-active', editor.isActive('orderedList'));
    
    const isHeading = editor.isActive('heading');
    document.getElementById('bubble-heading')?.classList.toggle('is-active', isHeading);
  });
  
  // Prevent bubble menu from showing on initial load
  setTimeout(() => {
    editor.view.updateState(editor.state);
  }, 100);
  
  // Add paste event handler for markdown conversion
  const editorEl = document.getElementById('tiptapEditor');
  editorEl.addEventListener('paste', (e) => {
    // Get plain text from clipboard
    const text = e.clipboardData?.getData('text/plain');
    if (!text) return;
    
    // Check if text contains markdown patterns
    const hasMarkdown = detectMarkdown(text);
    
    if (hasMarkdown) {
      // Prevent default paste behavior
      e.preventDefault();
      
      // Convert markdown to HTML
      const html = marked.parse(text);
      const processedHtml = convertCheckboxesToTipTap(html);
      
      // Insert the HTML content
      editor.commands.insertContent(processedHtml);
    }
    // If not markdown, allow normal paste behavior
  });
  
  // Setup table context menu
  setupTableContextMenu(document.getElementById('tiptapEditor'));
  
  // Handle link clicks
  const editorElement = document.getElementById('tiptapEditor');
  editorElement.addEventListener('click', async (e) => {
    const link = e.target.closest('a');
    if (!link) return;
    
    const href = link.getAttribute('href');
    if (!href) return;
    
    // Check if it's a CRD link in our repo
    const crdPattern = /github\.com\/cassie-groos_unilygh\/crd-editor-test\/blob\/main\/crds\/(.*\.md)/;
    const match = href.match(crdPattern);
    
    if (match && !e.ctrlKey && !e.metaKey) {
      // CRD link clicked without modifier key - load in editor
      e.preventDefault();
      
      const filePath = 'crds/' + match[1];
      
      // Track as recent
      trackRecentCrd(filePath);
      
      // Load the file (this will be handled by the loadFile function from files.js)
      // We need to trigger the file loading mechanism
      const loadFileEvent = new CustomEvent('loadCrdFile', { detail: { path: filePath } });
      document.dispatchEvent(loadFileEvent);
    }
    // Otherwise, Ctrl/Cmd+Click or external link opens in new tab (default behavior)
  });
  
  // Track changes for save button state
  let hasUnsavedChanges = false;

  editor.on('update', ({ editor, transaction }) => {
    // Only track user-initiated changes, not programmatic ones
    if (!transaction.docChanged) return;
    
    if (!hasUnsavedChanges) {
      hasUnsavedChanges = true;
      const saveBtn = document.getElementById('saveBtn');
      if (saveBtn) {
        saveBtn.classList.add('has-changes');
      }
    }
  });

  // Export function to reset after save
  window.resetEditorChanges = () => {
    hasUnsavedChanges = false;
    document.getElementById('saveBtn')?.classList.remove('has-changes');
  };

  return editor;
}

export function updateEditorContent(content) {
  if (editor) {
    // console.log('📝 Original markdown:', content.substring(0, 500));
    
    const html = marked.parse(content);
    // console.log('🔄 Marked HTML:', html.substring(0, 500));
    
    const processedHtml = convertCheckboxesToTipTap(html);
    // console.log('✅ Processed HTML:', processedHtml.substring(0, 500));
    
    editor.commands.setContent(processedHtml);
    
    // DEBUG: Uncomment to inspect TipTap rendering
    // setTimeout(() => {
    //   console.log('=== TIPTAP RENDERED HTML ===');
    //   console.log(editor.getHTML());
    //   console.log('=== RAW PROSEMIRROR ===');
    //   console.log(document.querySelector('.ProseMirror').innerHTML);
    // }, 500);
  }
}

export function getEditorHTML() {
  return editor ? editor.getHTML() : '';
}

export function getEditorMarkdown() {
  if (!editor) return '';
  
  const html = editor.getHTML();
  // console.log('🔄 Converting HTML to markdown');
  // console.log('🔄 Sample HTML (task lists):', html.substring(html.indexOf('taskList'), html.indexOf('taskList') + 500));
  
  const markdown = turndownService.turndown(html);
  // console.log('🔄 Sample markdown (checkboxes):', markdown.substring(4000, 5000));
  // console.log('🔄 Checkbox count:', markdown.match(/-\s*\[[ xX]\]/g)?.length || 0);
  
  return markdown;
}

export function hasEditor() {
  return editor !== null;
}

export function getEditor() {
  return editor;
}

// Table context menu
let tableContextMenu = null;

function createTableContextMenu() {
  const menu = document.createElement('div');
  menu.className = 'table-context-menu';
  menu.innerHTML = `
    <div class="table-context-item" data-action="addRowBefore">Insert row above</div>
    <div class="table-context-item" data-action="addRowAfter">Insert row below</div>
    <div class="table-context-item" data-action="deleteRow">Delete row</div>
    <div class="table-context-divider"></div>
    <div class="table-context-item" data-action="addColumnBefore">Insert column left</div>
    <div class="table-context-item" data-action="addColumnAfter">Insert column right</div>
    <div class="table-context-item" data-action="deleteColumn">Delete column</div>
    <div class="table-context-divider"></div>
    <div class="table-context-item" data-action="deleteTable">Delete table</div>
  `;
  
  menu.addEventListener('click', (e) => {
    const item = e.target.closest('.table-context-item');
    if (!item || !editor) return;
    
    const action = item.dataset.action;
    
    switch(action) {
      case 'addRowBefore':
        editor.chain().focus().addRowBefore().run();
        break;
      case 'addRowAfter':
        editor.chain().focus().addRowAfter().run();
        break;
      case 'deleteRow':
        editor.chain().focus().deleteRow().run();
        break;
      case 'addColumnBefore':
        editor.chain().focus().addColumnBefore().run();
        break;
      case 'addColumnAfter':
        editor.chain().focus().addColumnAfter().run();
        break;
      case 'deleteColumn':
        editor.chain().focus().deleteColumn().run();
        break;
      case 'deleteTable':
        editor.chain().focus().deleteTable().run();
        break;
    }
    
    hideTableContextMenu();
  });
  
  document.body.appendChild(menu);
  return menu;
}

function showTableContextMenu(x, y) {
  if (!tableContextMenu) {
    tableContextMenu = createTableContextMenu();
  }
  
  tableContextMenu.style.position = 'fixed';
  tableContextMenu.style.left = x + 'px';
  tableContextMenu.style.top = y + 'px';
  tableContextMenu.style.display = 'block';
}

function hideTableContextMenu() {
  if (tableContextMenu) {
    tableContextMenu.style.display = 'none';
  }
}

// Close menu when clicking elsewhere
document.addEventListener('click', (e) => {
  if (tableContextMenu && !e.target.closest('.table-context-menu')) {
    hideTableContextMenu();
  }
});

function setupTableContextMenu(editorElement) {
  // Handle right-click (contextmenu event)
  editorElement.addEventListener('contextmenu', (e) => {
    const cell = e.target.closest('td, th');
    if (cell) {
      e.preventDefault();
      e.stopPropagation();
      showTableContextMenu(e.clientX, e.clientY);
    }
  });
  
  // Also handle Ctrl+Click for Mac
  editorElement.addEventListener('click', (e) => {
    if (e.ctrlKey) {
      const cell = e.target.closest('td, th');
      if (cell) {
        e.preventDefault();
        e.stopPropagation();
        showTableContextMenu(e.clientX, e.clientY);
      }
    }
  });
}